import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { UserService } from '@sharedcommon/services/user.service';
import { User } from '@sharedcommon/model/user.model';
import { SampleDto } from '@sharedcommon/model/sample/sample.model';
import { SupplierDto } from '@sharedcommon/model/organization/supplier.model';
import { CustomerDto } from '@sharedcommon/model/organization/customer.model';
import { SampleSupplierDto } from '@sharedcommon/model/sample/sample-supplier.model';
import { FunctionUtils } from '@sharedcommon/util/functions.util';
import { Constants } from '@sharedcommon/util/constants.util';

declare type SelectionName =  'customer'|'supplier';

@Component({
  selector: 'neq-samples-optional-properties',
  templateUrl: './samples-optional-properties.component.html',
  styleUrls: [ './samples-optional-properties.component.scss' ]
})
export class NeqSampleOptionalProperties implements OnChanges, AfterViewInit {

  @Input() optionalForm: FormGroup;
  @Input() notesForm: FormControl;
  @Input() sample: SampleDto;
  @Input() selectedSupplier: SupplierDto;
  @Input() canSave: boolean;
  @Input() selectedCustomer: CustomerDto;
  @Output() saveClick = new EventEmitter<void>();

  user: User;
  public isReadOnly = true;

  constructor(
    private readonly userService: UserService,
    private readonly cd: ChangeDetectorRef
  ) {
    this.user = this.userService.getUser();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.sample?.currentValue) {
      if (this.optionalForm.pristine) {
        // Fill the mandatory form only if the user has not touched this before
        // Used to keep the state of the tab when we get back to it
        this.fillOptionalForm(this.sample);
      }
    }
    if (changes?.selectedSupplier) {
      this.onSupplierSelectionChange();
    }
    if (changes?.selectedCustomer) {
      this.onCustomerSelectionChange();
    }
  }

  ngAfterViewInit(): void {
    this.cd.detectChanges();
  }

  /**
   * Update selected customer form
   */
  private onCustomerSelectionChange(): void {
    const selectedCustomerForm = this.optionalForm.get("selectedCustomer");
    if (this.selectedCustomer) {
      selectedCustomerForm.patchValue(this.selectedCustomer);
    } else {
      selectedCustomerForm.reset();
    }
  }

  /**
   * Update the selected supplier form
   */
  private onSupplierSelectionChange(): void {
    const selectedSupplierForm = this.optionalForm.get("selectedSupplier");
    if (this.selectedSupplier) {
      selectedSupplierForm.patchValue(this.selectedSupplier);
    } else {
      selectedSupplierForm.reset();
    }
  }

  /**
   * Create an empty mandatory skeleton form
   *
   * @returns the form for mandatory datas
   */
  public static createEmptyOptionalForm(): FormGroup {
    return new FormGroup({
      orderNumber: new FormControl(null),
      contractCode: new FormControl(null),
      rawWeight: new FormControl(null, Validators.pattern(Constants.REGEX_DECIMAL)),
      variety: new FormControl(null),
      plot: new FormControl(null),
      silo: new FormControl(null),
      tag: new FormControl(null),
      chopCode: new FormControl(null),
      chop: new FormControl(null),
      samplingDate: new FormControl(null),
      receptionDate: new FormControl(null),
      harvestDate: new FormControl(null),
      technicianObservation: new FormControl(null),
      farm: new FormGroup({
        postcode: new FormControl(null),
        region: new FormControl(null),
        code: new FormControl(null)
      }),
      // lab comment
      supplierComment: new FormControl(null),
      supplierFileNumber: new FormControl(null),
      supplierSampleVersion: new FormControl(null),
      // lab form of the user
      supplier: this.createSupplierFormGrp(false),
      // the lab of the selected supplier in mandatory properties part
      selectedSupplier: this.createSupplierFormGrp(true),
      // Indicates if the supplier form to take into account is the selected one or not
      isSupplierSelected: new FormControl(true),
      customer: this.createCustomerFormGrp(false),
      // the customer from entity if there is
      selectedCustomer: this.createCustomerFormGrp(true),
      // Indicates if the customer to take into acocunt is the one from the entity selected in mandatory properties part
      isCustomerSelected: new FormControl(true)
    });
  }

  /**
   * Create the form group for customer part
   *
   * @param disable at true if the initial state is disabled
   * @returns empty form group for customer
   */
  private static createCustomerFormGrp(disable: boolean): FormGroup {
    const form = new FormGroup({
      sendNumber: new FormControl(null),
      batchCode: new FormControl(null),
      address: new FormControl(null),
      address1: new FormControl(null),
      postcode: new FormControl(null),
      city: new FormControl(null),
      country: new FormControl(null),
      distributor: new FormControl(null),
      commercialRegion: new FormControl(null)
    });
    if (disable) {
      form.disable();
    }
    return form;
  }

  /**
   * Create the form group for supplier
   *
   * @param disable at true if the initial state is disabled
   * @returns empty form for supplier
   */
  private static createSupplierFormGrp(disable: boolean): FormGroup {
    const form = new FormGroup({
      address: new FormControl(null),
      address1: new FormControl(null),
      city: new FormControl(null),
      country: new FormControl(null),
      email: new FormControl(null, Validators.email),
      telephone: new FormControl(null)
    });
    if (disable) {
      form.disable();
    }
    return form;
  }

  /**
   * Fill the form with the sample datas
   *
   * @param sample the sample that contains the data to fill in the form
   */
  private fillOptionalForm(sample: SampleDto): void {
    NeqSampleOptionalProperties.fillOptionalForm(sample, this.optionalForm);
  }

  /**
   * Fill the form with the sample datas
   *
   * @param sample        the sample that contains the data to fill in the form
   * @param optionalForm  the mandatory form which has been previously created by the method {@link createEmptyOptionalForm }
   */
  public static fillOptionalForm(sample: SampleDto, optionalForm: FormGroup): void {
    sample = {
      ...sample,
      // Convert the dates which can be in epoch format in real date
      // to be understandable by the form control
      samplingDate: FunctionUtils.convertToDate(sample.samplingDate),
      harvestDate: FunctionUtils.convertToDate(sample.harvestDate),
      receptionDate: FunctionUtils.convertToDate(sample.receptionDate)
    }
    optionalForm.patchValue(sample);
    optionalForm.get("supplierComment").setValue(sample?.supplier?.comment);
    optionalForm.get("supplierSampleVersion").setValue(sample?.supplier?.sampleVersion);
    optionalForm.get("supplierFileNumber").setValue(sample?.supplier?.fileNumber);
  }


  /**
   * return the the sample according the optional form
   *
   * @param optionalForm  the form which has the optional sample attribute
   */
  public static returnSampleFromForm(optionalForm: FormGroup): SampleDto {
    const sampleFormVal: SampleDto = optionalForm.value;
    sampleFormVal.customer = this.getCustomerForm(optionalForm).value;
    const supplier: SampleSupplierDto = this.getSupplierForm(optionalForm).value ;
    supplier.comment = optionalForm.get('supplierComment').value;
    supplier.fileNumber = optionalForm.get('supplierFileNumber').value;
    supplier.sampleVersion = optionalForm.get('supplierSampleVersion').value;
    sampleFormVal.supplier = supplier;
    return sampleFormVal;
  }

  /**
   * Return the supplier form to display according the user selection
   *
   * @returns the supplier form to take into account
   */
  getSupplierForm(): FormGroup {
    return NeqSampleOptionalProperties.getSupplierForm(this.optionalForm);
  }

  /**
   * Return the supplier form to take into account according the user selection
   *
   * @param optionalForm the optional form from what the supplier forms and the user selection is from
   * @returns the supplier form to take into account
   */
  static getSupplierForm(optionalForm: FormGroup): FormGroup {
    return (this.isSelectionMode('supplier', optionalForm) ? optionalForm.get("selectedSupplier") : optionalForm.get("supplier")) as FormGroup;
  }

  /**
   * Check if the form to take into account is the selected one or not
   *
   * @param selectedName the selected name from what the form name will be determined
   * @returns at true if we have to take into account the forme of the selected ones
   */
  isFormSelected(selectedName: SelectionName): boolean {
    return NeqSampleOptionalProperties.isSelectionMode(selectedName, this.optionalForm) ;
  }

  /**
   * Indicates that it is the selected form to take into account
   *
   * @param selectedName the selected name from what the form name will be determined
   */
  selectForm(selectedName: SelectionName): void {
    const isSelectForm = NeqSampleOptionalProperties.returnSelectionForm(selectedName, this.optionalForm);
    isSelectForm.setValue(true);
    // Mark as dirty to indirectly enbale the save button because this button is on pristine flag of form
    isSelectForm.markAsDirty();
  }

  /**
   * Indicates that it is the unselected form to take into account
   *
   * @param selectedName the selected name from what the form name will be determined
   */
  unselectForm(selectedName: SelectionName): void {
    const isSelectForm = NeqSampleOptionalProperties.returnSelectionForm(selectedName, this.optionalForm);
    isSelectForm.setValue(false);
    // Mark as dirty to indirectly enbale the save button because this button is on pristine flag of form
    isSelectForm.markAsDirty();
  }

  /**
   * Return the form name which indicates which form to take into account accorgin form the selectedName
   *
   * @param selectedName the selection name from what the formName will be determined
   * @returns the corresponding form name
   */
  private static returnFormNameFromSelectedName(selectedName: SelectionName): string {
    return selectedName === 'supplier' ? 'isSupplierSelected' : 'isCustomerSelected';
  }

  /**
   * Return the form which indicates which form to take into account
   *
   * @param selectedName the name from what the form name will be determined
   * @param optionalForm the form from what the returned form will be from
   * @returns the corresponding form from what we will know which form has to be taken into account (selected one or not)
   */
  private static returnSelectionForm(selectedName: SelectionName, optionalForm: FormGroup): FormControl {
    return optionalForm.get(NeqSampleOptionalProperties.returnFormNameFromSelectedName(selectedName)) as FormControl;
  }

  /**
   * Estimate if the form to take into account is the selected one or not
   *
   * @param selectedName the name from what the form name will be determined
   * @param optionalForm the form from what the estimation must be done
   * @returns true if the form to take into account is the selected one
   */
  private static isSelectionMode(selectedName: SelectionName, optionalForm: FormGroup): boolean {
    return this.returnSelectionForm(selectedName, optionalForm).value;
  }

  /**
   * Return the customer form to take into account
   *
   * @returns the customer form to take into account
   */
  getCustomerForm(): FormGroup {
    return NeqSampleOptionalProperties.getCustomerForm(this.optionalForm);
  }

  /**
   * Return the customer form to take into account
   *
   * @param optionalForm the form from what the custoer forms are
   * @returns the customer form to take into account
   */
  private static getCustomerForm(optionalForm: FormGroup): FormGroup {
    return (this.isSelectionMode('customer', optionalForm) ? optionalForm.get("selectedCustomer") : optionalForm.get("customer")) as FormGroup;
  }

  isFormInvalidAndTouched(pathName: string): boolean {
    const form = this.optionalForm.get(pathName);
    return form.invalid && form.touched;
  }

  returnDateFormat() {
    return this.userService.returnUserDateFormat();
  }

  save() {
    this.saveClick.next();
  }
}
